import React, { useEffect, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import RichTextEditor from '../../components/RichTextEditor';
import { isRichTextEmpty, normalizeWhatsappText } from '../../utils/richText';
import { apiUrl, fetchJson } from '../../apiBase';

const parseDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};
const dateOnly = (value) => value instanceof Date && !Number.isNaN(value.getTime())
  ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` : '';
const requestId = () => window.crypto?.randomUUID?.() || `hw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const gradeLabel = (value) => /^\d+$/.test(value) ? `${value}. sınıf` : value;
const fieldLabelSx = { mb: 1, fontWeight: 600, color: 'text.primary' };
const selectionChipSx = {
  minHeight: 44, height: 'auto', maxWidth: '100%', borderRadius: 1,
  border: '1px solid', fontSize: '0.875rem', lineHeight: 1.4,
  '& .MuiChip-label': { px: 1.5, py: 1, whiteSpace: 'normal', overflowWrap: 'break-word', textOverflow: 'clip', textAlign: 'left' },
  '&[aria-pressed="false"]': {
    bgcolor: 'grey.50', borderColor: 'divider', color: 'text.primary', fontWeight: 500,
    '&:hover': { bgcolor: 'grey.100' },
  },
  '&[aria-pressed="true"]': {
    bgcolor: 'primary.main', borderColor: 'primary.main', color: 'primary.contrastText', fontWeight: 600,
    '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
  },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
};

export default function CreateHomeworkDialog({ schoolId, user, initialClassId, onClose, onCreated, onError }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTeacher = user?.role === 'teacher';
  const [teachers, setTeachers] = useState([]);
  const [teacherSelection, setTeacherSelection] = useState(null);
  const teacherId = teacherSelection?.schoolId === schoolId ? teacherSelection.id : '';
  const contextKey = JSON.stringify([schoolId, isTeacher, isTeacher ? user?.teacher_id : teacherId]);
  const [loadedOptions, setLoadedOptions] = useState(null);
  const options = loadedOptions?.contextKey === contextKey ? loadedOptions.data : null;
  const [grade, setGrade] = useState('');
  const [selected, setSelected] = useState([]);
  const [lessonId, setLessonId] = useState('');
  const [otherLessonsOpen, setOtherLessonsOpen] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [uncertain, setUncertain] = useState(false);
  const [discard, setDiscard] = useState(false);
  const initialized = useRef(false);
  const loadedContext = useRef('');
  const dateTouched = useRef(false);
  const inFlight = useRef(false);
  const pending = useRef(null);
  const mounted = useRef(true);
  const otherLessonsToggle = useRef(null);
  const sections = options?.assigned_sections || [];
  const onlySection = sections.length === 1 ? sections[0] : null;
  const grades = [...new Set(sections.map((item) => String(item.grade_level)))].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b, 'tr'));
  const targets = sections.filter((item) => String(item.grade_level) === grade);
  const lessons = options?.lessons_by_grade?.[grade] || [];
  const quickLessons = lessons.slice(0, 3);
  const otherLessons = lessons.slice(3);
  const selectedLesson = lessons.find((item) => String(item.id) === lessonId);
  const locked = saving || uncertain;
  const valid = options && !optionsFailed && selected.length > 0 && selected.every((id) => targets.some((item) => String(item.id) === id))
    && selectedLesson && dateOnly(dueDate) && !isRichTextEmpty(description);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (isTeacher) return undefined;
    let current = true;
    setTeachers([]);
    fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`)).then(async (response) => {
      if (!response.ok) throw new Error('Öğretmenler yüklenemedi.');
      const data = await response.json();
      if (current) setTeachers(data);
    }).catch((reason) => { if (current) setError(reason.message); });
    return () => { current = false; };
  }, [schoolId, isTeacher]);

  useEffect(() => {
    if (loadedContext.current !== contextKey) {
      loadedContext.current = contextKey;
      initialized.current = false;
      dateTouched.current = false;
      setLoadedOptions(null);
      setGrade('');
      setSelected([]);
      setLessonId('');
      setDueDate(null);
      setOptionsFailed(false);
      setError('');
    }
    if (!isTeacher && !teacherId) { setLoading(false); return undefined; }
    let current = true;
    setLoading(true);
    const query = new URLSearchParams();
    if (!isTeacher) query.set('teacher_id', teacherId);
    fetchJson(apiUrl(`/admin/homework-create-options/${encodeURIComponent(schoolId)}?${query}`)).then(async (response) => {
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Ödev bilgileri yüklenemedi.');
      if (!current) return;
      setOptionsFailed(false);
      setLoadedOptions({ contextKey, data });
      if (!dateTouched.current) setDueDate(parseDate(data.suggested_due_date));
      const assigned = data.assigned_sections || [];
      const levels = [...new Set(assigned.map((item) => String(item.grade_level)))];
      if (!initialized.current) {
        initialized.current = true;
        const initial = assigned.find((item) => String(item.id) === String(initialClassId)) || (assigned.length === 1 ? assigned[0] : null);
        setGrade(initial ? String(initial.grade_level) : levels.length === 1 ? levels[0] : '');
        setSelected(initial ? [String(initial.id)] : []);
      } else {
        setGrade((currentGrade) => levels.includes(currentGrade) ? currentGrade : levels.length === 1 ? levels[0] : '');
        setSelected((ids) => assigned.length === 1 ? [String(assigned[0].id)] : ids.filter((id) => assigned.some((item) => String(item.id) === id)));
      }
    }).catch((reason) => { if (current) { setOptionsFailed(true); setError(reason.message); } })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [schoolId, isTeacher, teacherId, contextKey, reload, initialClassId]);

  useEffect(() => {
    setOtherLessonsOpen(false);
    if (uncertain) return;
    setLessonId((current) => {
      if (lessons.some((item) => String(item.id) === current)) return current;
      return lessons.length === 1 ? String(lessons[0].id) : '';
    });
  }, [options, grade, uncertain]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeGrade = (next) => {
    if (next === grade) return;
    setGrade(next);
    const available = sections.filter((item) => String(item.grade_level) === next);
    setSelected(available.length === 1 ? [String(available[0].id)] : []);
    setLessonId('');
    setError('');
  };
  const close = () => {
    if (inFlight.current) return;
    if (uncertain) { setError('Gönderim henüz doğrulanamadı. Aynı işlemi yeniden deneyerek sonucu doğrulayın.'); return; }
    if (!isRichTextEmpty(description)) setDiscard(true);
    else onClose();
  };
  const chooseLesson = (item) => {
    if (otherLessonsOpen && otherLessons.some((lesson) => lesson.id === item.id)) {
      otherLessonsToggle.current?.focus({ preventScroll: true });
    }
    setLessonId(String(item.id));
    setOtherLessonsOpen(false);
  };

  const submit = async () => {
    if (inFlight.current || (!valid && !pending.current) || loading) return;
    inFlight.current = true;
    setSaving(true);
    setError('');
    const payload = pending.current || {
      class_ids: selected.map(Number), lesson_id: Number(lessonId), due_date: dateOnly(dueDate),
      description: normalizeWhatsappText(description), client_request_id: requestId(),
      ...(!isTeacher ? { teacher_id: Number(teacherId) } : {}),
    };
    pending.current = payload;
    let created;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.signal,
      });
      if (!response.ok) {
        if (response.status < 500 && response.status !== 409 && response.status !== 408) {
          pending.current = null;
          setUncertain(false);
          setReload((value) => value + 1);
        } else setUncertain(true);
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Ödev gönderilemedi. Aynı işlemi yeniden deneyin.');
      }
      created = await response.json();
      if (!created.id) throw new Error('Gönderim sonucu doğrulanamadı.');
    } catch (reason) {
      if (mounted.current) {
        setUncertain(Boolean(pending.current));
        setError(pending.current ? 'Bağlantı nedeniyle gönderim doğrulanamadı. Aynı işlem kimliğiyle yeniden deneyin.' : reason.message);
        setSaving(false);
      }
      inFlight.current = false;
      return;
    } finally {
      window.clearTimeout(timeout);
    }
    // The existing notification endpoint has its own dedupe/retry behavior.
    // A notification failure never causes another create request.
    let notificationFailed = false;
    const notificationController = new AbortController();
    const notificationTimeout = window.setTimeout(() => notificationController.abort(), 30000);
    try {
      const response = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}/${encodeURIComponent(created.id)}/send-messages`), { method: 'POST', signal: notificationController.signal });
      if (!response.ok) throw new Error();
    } catch (_) {
      notificationFailed = true;
    } finally {
      window.clearTimeout(notificationTimeout);
    }
    if (mounted.current) {
      onCreated(created);
      if (notificationFailed) onError?.('Ödev kaydedildi; WhatsApp bildirimi başlatılamadı. Listeden tekrar gönderebilirsiniz.');
    }
    inFlight.current = false;
  };

  return (
    <Dialog open onClose={close} fullScreen={mobile} fullWidth maxWidth="sm" aria-labelledby="new-homework-title">
      <DialogTitle id="new-homework-title" sx={{ px: { xs: 2, sm: 3 }, py: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Yeni Ödev <IconButton aria-label="Formu kapat" onClick={close} disabled={saving}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {optionsFailed && <Button onClick={() => setReload((value) => value + 1)}>Bilgileri Yenile</Button>}
        {loading && <Box role="status" aria-label="Ödev bilgileri yükleniyor"><CircularProgress size={20} /></Box>}
        {!isTeacher && <TextField select label="Öğretmen" fullWidth size="small" value={teacherId} disabled={locked} sx={{ mb: 1.5 }} onChange={(event) => {
          setTeacherSelection({ schoolId, id: event.target.value });
        }}>{teachers.map((teacher) => <MenuItem key={teacher.id} value={String(teacher.id)}>{teacher.name} {teacher.surname}</MenuItem>)}</TextField>}
        {options && <>
          {onlySection ? <Box role="group" aria-label="Sınıf / Şube" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 1.5, py: 1, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Sınıf / Şube</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{onlySection.name} · {gradeLabel(String(onlySection.grade_level))}</Typography>
          </Box> : <>
          <Typography component="h2" variant="subtitle2" sx={fieldLabelSx}>Sınıf</Typography>
          <Box role="group" aria-label="Sınıf seviyeleri" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {grades.map((level) => <Chip key={level} component="button" type="button" label={gradeLabel(level)} aria-pressed={grade === level}
              color={grade === level ? 'primary' : 'default'} variant={grade === level ? 'filled' : 'outlined'}
              disabled={locked} onClick={() => changeGrade(level)} sx={selectionChipSx} />)}
          </Box>
          {!sections.length && <Alert severity="info">Ödev verebileceğiniz sınıf bulunamadı. Kurum yöneticinizden atamanızı kontrol etmesini isteyin.</Alert>}
          {sections.length > 0 && !grade && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Şubeleri görmek için bir sınıf seçin.</Typography>}
          {grade && <>
          <Typography component="h2" variant="subtitle2" sx={fieldLabelSx}>Şube</Typography>
          <Box role="group" aria-label="Şubeler" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {targets.map((item) => <Chip key={item.id} component="button" type="button" label={item.name} aria-pressed={selected.includes(String(item.id))}
              color={selected.includes(String(item.id)) ? 'primary' : 'default'} variant={selected.includes(String(item.id)) ? 'filled' : 'outlined'}
              disabled={locked} onClick={() => setSelected((ids) => ids.includes(String(item.id)) ? ids.filter((id) => id !== String(item.id)) : [...ids, String(item.id)])} sx={selectionChipSx} />)}
          </Box>
          </>}
          {grade && !selected.length && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Ödev göndermek için en az bir şube seçin.</Typography>}
          </>}
          <Typography component="h2" variant="subtitle2" sx={{ ...fieldLabelSx, mt: 0.5 }}>Ders</Typography>
          {grade && !lessons.length && <Alert severity="info">Bu sınıf için uygun ders bulunamadı. Branş atamanızı kurum yöneticinizden kontrol ettirin.</Alert>}
          {lessons.length === 1 ? <Typography sx={{ mb: 1.5, fontWeight: 600 }}>{lessons[0].name}</Typography> : <Box role="group" aria-label="Dersler" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {quickLessons.map((item) => <Chip component="button" type="button" key={item.id} label={item.name} aria-pressed={String(item.id) === lessonId}
              color={String(item.id) === lessonId ? 'primary' : 'default'} variant={String(item.id) === lessonId ? 'filled' : 'outlined'}
              onClick={() => chooseLesson(item)} disabled={locked} sx={selectionChipSx} />)}
          </Box>}
          {otherLessons.length > 0 && <Accordion disableGutters elevation={0} expanded={otherLessonsOpen} disabled={locked}
            onChange={(_, expanded) => setOtherLessonsOpen(expanded)} TransitionProps={{ timeout: 180, unmountOnExit: true }}
            sx={{ mb: 1.5, bgcolor: 'transparent', '&:before': { display: 'none' }, '&.Mui-disabled': { bgcolor: 'transparent' } }}>
            <AccordionSummary ref={otherLessonsToggle} expandIcon={<ExpandMore fontSize="small" />}
              id="homework-other-lessons-toggle" aria-controls="homework-other-lessons"
              sx={{ px: 0, minHeight: 44, '&.Mui-expanded': { minHeight: 44 }, color: 'primary.main', '& .MuiAccordionSummary-content': { my: 1 }, '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 } }}>
              <Typography variant="body2" color="inherit" sx={{ fontWeight: 500 }}>Diğer Dersler ({otherLessons.length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 0.5 }}>
              <Box role="group" aria-label="Diğer dersler" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {otherLessons.map((item) => <Chip component="button" type="button" key={item.id} label={item.name} aria-pressed={String(item.id) === lessonId}
                  color={String(item.id) === lessonId ? 'primary' : 'default'} variant={String(item.id) === lessonId ? 'filled' : 'outlined'}
                  onClick={() => chooseLesson(item)} disabled={locked} sx={selectionChipSx} />)}
              </Box>
            </AccordionDetails>
          </Accordion>}
          {selectedLesson && !otherLessonsOpen && !quickLessons.some((item) => String(item.id) === lessonId) &&
            <Box role="group" aria-label="Seçili ders" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: 'fit-content', maxWidth: '100%', px: 1, py: 0.75, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Check sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Seçilen ders: {selectedLesson.name}</Typography>
            </Box>}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
            <DatePicker label="Son Teslim Tarihi *" value={dueDate} disabled={locked} format="dd.MM.yyyy" onChange={(value) => { dateTouched.current = true; setDueDate(value); }}
              slotProps={{ textField: { fullWidth: true, size: 'small', sx: { mt: 1.5, mb: 2 }, error: !dateOnly(dueDate), helperText: !dateOnly(dueDate) ? 'Geçerli bir son teslim tarihi seçin.' : '' } }} />
          </LocalizationProvider>
          <Typography component="h2" variant="subtitle2" sx={fieldLabelSx}>Ödev Açıklaması *</Typography>
          <Box component="fieldset" disabled={locked} sx={{ border: 0, p: 0, m: 0, minWidth: 0, ...(locked ? { pointerEvents: 'none', opacity: 0.7 } : {}) }}>
            <RichTextEditor value={description} onChange={(value) => { if (!locked) setDescription(value); }} placeholder="Ödevi buraya yazın..." minCollapsedHeight={mobile ? 160 : 140} />
          </Box>
        </>}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 'max(12px, env(safe-area-inset-bottom))' }}>
        <Button fullWidth variant="contained" onClick={submit} disabled={saving || loading || (!uncertain && !valid)}
          sx={{ minHeight: 44, borderRadius: 1, fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.secondary' } }}>
          {saving ? 'Gönderiliyor…' : uncertain ? 'Gönderimi Yeniden Doğrula' : `Ödevi Gönder${selected.length > 1 ? ` (${selected.length} şube)` : ''}`}
        </Button>
      </DialogActions>
      <Dialog open={discard} onClose={() => setDiscard(false)} aria-labelledby="discard-homework-title"><DialogTitle id="discard-homework-title">Değişiklikleri sil?</DialogTitle><DialogActions><Button onClick={() => setDiscard(false)}>Devam Et</Button><Button color="error" onClick={onClose}>Değişiklikleri Sil</Button></DialogActions></Dialog>
    </Dialog>
  );
}
